import { Terminal, ArrowClockwise, ShareNetwork } from "@/components/ui/icons";
import { cn } from "@/lib/utils";
import type { CommandHistoryEntry } from "../command-tracker";

interface CommandHistoryCardProps {
  entry: CommandHistoryEntry;
  compact?: boolean;
  onRerun?: (command: string) => void;
  onShare?: (entry: CommandHistoryEntry) => void;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const rem = Math.round(s % 60);
  return `${m}m ${rem}s`;
}

export function CommandHistoryCard({
  entry,
  compact,
  onRerun,
  onShare,
}: CommandHistoryCardProps) {
  const isSuccess = entry.exitCode === 0;

  return (
    <div
      className={cn(
        "my-1 w-full max-w-[34rem] rounded bg-foreground/[0.03] overflow-hidden",
        compact && "text-[12px]",
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/50">
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground min-w-0">
          <Terminal size={12} weight="Bold" />
          <code className="font-mono text-foreground truncate max-w-[30ch]">
            {entry.command}
          </code>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span
            className={cn(
              "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium",
              isSuccess
                ? "bg-success/15 text-success"
                : "bg-destructive/15 text-destructive",
            )}
          >
            {isSuccess ? "0" : entry.exitCode}
          </span>
          <span className="text-[10px] text-muted-foreground">
            {formatDuration(entry.duration)}
          </span>
        </div>
      </div>

      {/* Output preview */}
      {entry.outputPreview && (
        <div className="px-3 py-1.5 max-h-[120px] overflow-y-auto">
          <pre className="font-mono text-[11px] leading-[1.4] text-muted-foreground whitespace-pre-wrap break-all">
            {entry.outputPreview}
          </pre>
        </div>
      )}

      {/* Actions */}
      {(onRerun || onShare) && (
        <div className="flex items-center gap-1 px-3 py-1 border-t border-border/50">
          {onRerun && (
            <button
              onClick={() => onRerun(entry.command)}
              className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors px-1.5 py-0.5 rounded hover:bg-foreground/5"
            >
              <ArrowClockwise size={11} />
              Re-run
            </button>
          )}
          {onShare && (
            <button
              onClick={() => onShare(entry)}
              className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors px-1.5 py-0.5 rounded hover:bg-foreground/5"
            >
              <ShareNetwork size={11} />
              Share
            </button>
          )}
        </div>
      )}
    </div>
  );
}
